import { createContext, ReactNode, useContext } from "react"

// Generic helpers for the "logic / events / hooks / UI" view-data pattern (issue
// #1117): a data-acquisition component (the "hooks" layer -- fetch/subscribe via the
// centralized Data-layer store) builds a typed value and hands it to its
// presentational descendants via a dedicated context, while the presentational ("UI")
// component reads it with a single `useData()` hook instead of prop-drilling. Both
// `createViewData` and `withViewData` below are intentionally epic-/view-agnostic so
// any data-fetching view can adopt the same shape.

export interface ViewData<Data> {
  Context: React.Context<Data | undefined>;
  useData: () => Data;
}

// Creates a typed context + accessor hook pair for one view's data. `name` is used
// only for the error message thrown by `useData()` when a caller forgets to render
// the corresponding data-acquisition component above it in the tree.
export function createViewData<Data>(name: string): ViewData<Data> {
  const Context = createContext<Data | undefined>(undefined);
  function useData(): Data {
    const value = useContext(Context);
    if (value === undefined) {
      throw new Error(`useData for "${name}" was called outside its provider -- render the view's data-acquisition component (e.g. via withViewData) above it in the tree.`);
    }
    return value;
  }
  return { Context, useData };
}

export interface WithViewDataOptions<Props extends object> {
  // The data-acquisition component. Receives `Props` plus the injected `ui` element as
  // its React children.
  acquire: React.ComponentType<Props & { children?: ReactNode }>;
  // The presentational component, rendered as the acquisition component's child and
  // given the same `Props` -- typically reads the acquired data via the matching
  // `useData()` hook and uses `Props` for anything that isn't fetched/subscribed data
  // (e.g. static per-instance props from the caller).
  ui: React.ComponentType<Props>;
}

// Mounts a view's data-acquisition component with its presentational component as the
// child, passing the same props to both.
export function withViewData<Props extends object>({ acquire: Acquire, ui: Ui }: WithViewDataOptions<Props>): React.ComponentType<Props> {
  return function ViewDataView(props: Props) {
    return <Acquire {...props}><Ui {...props} /></Acquire>;
  };
}
